import React, { useState, useMemo } from 'react';
import { Tooltip } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FeeCategoriesProps } from './types';
import { FEE_CATEGORY_ICONS, FEE_CATEGORY_COLORS, ICONS } from './constants';
import { StyledTable, CategoryCell, CategoryName, StripeTooltip, AmountCell, StyledCard, TableWrapper, ActionButton } from './Styles';
import { useTheme } from '../../../context/ThemeContext';
import { ThemeProvider } from 'styled-components';
import type { ColumnsType } from 'antd/es/table';
import TablePagination from '../../../commonComponents/Pagination';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount || 0);
};

const formatPercentage = (value: number): string => {
  return `${(value || 0).toFixed(1)}%`;
};

interface FeeCategoryRow {
  key: string;
  category: string;
  amount: number;
  count: number;
  percentage: number;
}

const FeeCategories: React.FC<FeeCategoriesProps> = ({ feeCategories, onViewDetails, loading = false }) => {
  const { isDarkMode } = useTheme();
  const theme = { mode: isDarkMode ? 'dark' : 'light' } as const;
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  const allData: FeeCategoryRow[] = useMemo(() => {
    if (!feeCategories) return [];
    return Object.entries(feeCategories).map(([category, data]) => ({
      key: category,
      category,
      amount: data.amount,
      count: data.count,
      percentage: data.percentage,
    }));
  }, [feeCategories]);

  const paginatedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return allData.slice(startIndex, endIndex);
  }, [allData, currentPage, pageSize]);

  const handlePaginationChange = (page: number, size: number) => {
    setCurrentPage(page);
    setPageSize(size);
  };

  const columns: ColumnsType<FeeCategoryRow> = [
    {
      title: 'Category',
      dataIndex: 'category',
      key: 'category',
      render: (category: string) => {
        const isStripe = category === 'stripe';
        return (
          <CategoryCell>
            <FontAwesomeIcon
              icon={FEE_CATEGORY_ICONS[category] || ICONS.dollar}
              style={{ color: FEE_CATEGORY_COLORS[category] || '#8c8c8c' }}
            />
            <CategoryName isStripe={isStripe}>
              {category.replace(/([A-Z])/g, ' $1').trim()}
            </CategoryName>
            {isStripe && (
              <Tooltip title="Actual fees charged by Stripe for payment processing">
                <StripeTooltip>
                  <FontAwesomeIcon icon={ICONS.info} />
                </StripeTooltip>
              </Tooltip>
            )}
          </CategoryCell>
        );
      },
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'left',
      render: (amount: number, record: FeeCategoryRow) => (
        <AmountCell isstripe={record.category === 'stripe' ? 'true' : 'false'}>
          {formatCurrency(amount)}
        </AmountCell>
      ),
    },
    {
      title: 'Count',
      dataIndex: 'count',
      key: 'count',
      align: 'left',
      render: (count: number) => (count || 0).toLocaleString(),
    },
    {
      title: 'Percentage',
      dataIndex: 'percentage',
      key: 'percentage',
      align: 'left',
      render: (percentage: number) => formatPercentage(percentage),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: FeeCategoryRow) => (
        <ActionButton
          onClick={() => onViewDetails(record.category, {
            amount: record.amount,
            count: record.count,
            percentage: record.percentage,
          })}
        >
          <FontAwesomeIcon icon={ICONS.eye} />
          Details
        </ActionButton>
      ),
    },
  ];

  return (
    <ThemeProvider theme={theme}>
      <StyledCard>
        <TableWrapper>
          <StyledTable
            columns={columns as any}
            dataSource={paginatedData}
            pagination={false}
            scroll={{ x: 'max-content' }}
            loading={loading}
          />
        </TableWrapper>
        <TablePagination
          totalCount={allData.length}
          currentPage={currentPage}
          pageSize={pageSize}
          onChange={handlePaginationChange}
          totalLabel="Total categories"
          padding="16px 0px 0px 0px"
        />
      </StyledCard>
    </ThemeProvider>
  );
};

export default FeeCategories;
